import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Play, Plus, Sparkles, TestTube2, Trash2 } from 'lucide-react';
import { Button, Chip, Select, Switch } from '@nyce/admin-kit/ui';
import {
  ActionsRow,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  Tabs,
  useAsync,
  useNotice,
} from '../../../components/common';
import { useTargets } from '../../../identity/session';
import { monitorsFor, type Monitor } from '../../api/monitors';
import { useBuddyWrite } from '../../api/client';
import { buddyPath } from '../../shared';
import { AlertsPanel } from './Alerts';
import { describeCron } from './cron';
import { emptyForm, fromMonitor, toInput, type MonitorForm } from './form';
import { GenerateDialog } from './GenerateDialog';
import { MonitorEditor } from './MonitorEditor';
import { useLookups, useQueryState } from './parts';
import { useRunTest } from './RunTest';

type Tab = 'monitors' | 'alerts';
const SCOPE_FILTER = [
  { value: '', label: 'All scopes' },
  { value: 'global', label: 'Global' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'system', label: 'System' },
];

/** `/buddy/monitors`: the monitor definitions of one environment's nyce.buddy, plus the open alerts of all of them. */
export function BuddyMonitorsPage() {
  const { active: target } = useTargets();
  const canWrite = useBuddyWrite();
  const q = useQueryState();
  const tab: Tab = q.get('tab') === 'alerts' ? 'alerts' : 'monitors';
  const scope = q.get('scope');
  const scopeId = q.get('scopeId');
  const api = monitorsFor(target);
  const list = useAsync(
    () => api.list({ scope: scope || undefined, scopeId: scopeId || undefined }),
    [target, scope, scopeId]
  );
  const { systems, tenants, error: lookupError } = useLookups(target);
  const toast = useNotice();
  const [editing, setEditing] = useState<MonitorForm | null>(null);
  const [generating, setGenerating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const runTest = useRunTest(target, systems, toast, () => void list.reload());
  const items = list.data?.items ?? [];
  const names = useMemo(() => new Map((list.data?.items ?? []).map((m) => [m.id, m.name])), [list.data]);
  const tenantName = (id: string) => tenants.find((t) => t.id === id)?.name ?? id;
  const systemName = (id: string) => systems.find((s) => s.id === id)?.name ?? id;

  const act = async (m: Monitor, fn: () => Promise<unknown>, done?: string) => {
    setBusyId(m.id);
    try {
      await fn();
      await list.reload();
      if (done) toast.success(done);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };
  const save = async (f: MonitorForm) => {
    const r = toInput(f);
    if ('error' in r) throw new Error(r.error);
    if (f.id) await api.update(f.id, r.body);
    else await api.create(r.body);
    toast.success(`Saved ${f.name.trim()}.`);
    await list.reload();
  };

  const scopeIdOptions =
    scope === 'tenant'
      ? tenants.map((t) => ({ value: t.id, label: t.name }))
      : scope === 'system'
        ? systems.map((s) => ({ value: s.id, label: s.name }))
        : [];

  return (
    <Page
      title="Monitors"
      description="Scheduled nyce.buddy agents that watch queries and logs, then mail a report or investigate every new alert."
      actions={
        <div className="bd-toolbar">
          {tab === 'monitors' && (
            <>
              <Select
                aria-label="Scope"
                value={scope}
                onChange={(v) => q.set({ scope: v, scopeId: null })}
                options={SCOPE_FILTER}
              />
              {scopeIdOptions.length > 0 && (
                <Select
                  aria-label={scope === 'tenant' ? 'Tenant' : 'System'}
                  value={scopeId}
                  onChange={(v) => q.set({ scopeId: v })}
                  options={[
                    { value: '', label: scope === 'tenant' ? 'Any tenant' : 'Any system' },
                    ...scopeIdOptions,
                  ]}
                />
              )}
            </>
          )}
          {canWrite && (
            <>
              <Button variant="ghost" icon={<Sparkles size={14} />} onClick={() => setGenerating(true)}>
                Generate with AI
              </Button>
              <Button
                icon={<Plus size={14} />}
                onClick={() => setEditing(emptyForm('system', { systemId: systems[0]?.id }))}
              >
                New monitor
              </Button>
            </>
          )}
        </div>
      }
    >
      <Tabs
        value={tab}
        onChange={(v) =>
          q.set({ tab: v === 'monitors' ? null : v, status: null, system: null, monitor: null, alert: null })
        }
        options={[
          { value: 'monitors', label: 'Monitors' },
          { value: 'alerts', label: 'Alerts' },
        ]}
      />
      <ErrorBox error={list.error ?? lookupError} />
      {toast.element}
      {tab === 'alerts' && (
        <AlertsPanel
          target={target}
          monitorNames={names}
          systems={systems}
          canWrite={canWrite}
          notify={toast}
        />
      )}
      {tab === 'monitors' && (
        <>
          <Loading loading={list.loading && !list.data} />
          {list.data && !items.length && (
            <EmptyState
              card
              title={scope ? 'No monitors in this scope.' : 'No monitors yet.'}
              hint={
                canWrite
                  ? 'Create one by hand, or describe what to watch and let the model draft it.'
                  : undefined
              }
              action={
                canWrite && (
                  <Button
                    variant="secondary"
                    icon={<Sparkles size={14} />}
                    onClick={() => setGenerating(true)}
                  >
                    Generate with AI
                  </Button>
                )
              }
            />
          )}
          {items.length > 0 && (
            <div className="nyk-card">
              <TableScroller>
                <table className="nyk-ui-table nyk-cards" data-testid="monitors-table">
                  <thead>
                    <tr>
                      <th>Monitor</th>
                      <th>Scope</th>
                      <th>Mode</th>
                      <th>Schedule</th>
                      <th>Sources</th>
                      <th>Enabled</th>
                    </tr>
                  </thead>
                  {items.map((m) => (
                    <tbody key={m.id} className="nyk-row-group" data-monitor={m.id}>
                      <tr>
                        <td className="nyk-card-title">
                          <Link to={buddyPath(`/buddy/monitors/${encodeURIComponent(m.id)}`, target)}>
                            <b>{m.name}</b>
                          </Link>
                          {m.description && <div className="nyk-ui-hint bm-clip">{m.description}</div>}
                        </td>
                        <td data-label="Scope">
                          <Chip size="sm">{m.scope}</Chip>
                          {m.systemId && <div className="nyk-ui-hint">{systemName(m.systemId)}</div>}
                          {m.tenantId && !m.systemId && (
                            <div className="nyk-ui-hint">{tenantName(m.tenantId)}</div>
                          )}
                        </td>
                        <td data-label="Mode">
                          <Chip size="sm" className={m.runMode === 'agent' ? 'nyk-tone-blue' : undefined}>
                            {m.runMode}
                          </Chip>
                        </td>
                        <td data-label="Schedule">
                          {m.schedule ?? describeCron(m.cronExpression)}
                          <div className="nyk-ui-hint">
                            <code>{m.cronExpression}</code>
                          </div>
                        </td>
                        <td data-label="Sources">
                          {m.savedQueryId ? (
                            <span className="nyk-ui-hint">
                              saved chat answer{m.runAs === 'owner' ? ' · as owner' : ''}
                            </span>
                          ) : (
                            <>
                              {m.sources.length}
                              <div className="nyk-ui-hint">
                                {[...new Set(m.sources.map((s) => s.type))].join(', ')}
                              </div>
                            </>
                          )}
                        </td>
                        <td data-label="Enabled">
                          {canWrite ? (
                            <Switch
                              checked={m.enabled}
                              disabled={busyId === m.id}
                              onChange={(enabled) =>
                                void act(
                                  m,
                                  () => api.update(m.id, { enabled }),
                                  `${m.name} ${enabled ? 'enabled' : 'paused'}.`
                                )
                              }
                              label={m.enabled ? 'on' : 'off'}
                            />
                          ) : (
                            <Chip size="sm" className={m.enabled ? 'nyk-tone-green' : undefined}>
                              {m.enabled ? 'on' : 'off'}
                            </Chip>
                          )}
                        </td>
                      </tr>
                      {canWrite && (
                        <ActionsRow colSpan={6}>
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={<Pencil size={14} />}
                            onClick={() => setEditing(fromMonitor(m))}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={<TestTube2 size={14} />}
                            loading={runTest.busy === m.id}
                            onClick={() => runTest.start(m, 'test')}
                          >
                            Test
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={<Play size={14} />}
                            disabled={busyId === m.id}
                            onClick={() => runTest.start(m, 'run')}
                          >
                            Run now
                          </Button>
                          <ConfirmButton
                            size="sm"
                            variant="danger"
                            icon={<Trash2 size={14} />}
                            message={`Delete monitor "${m.name}" with its runs and alerts?`}
                            onConfirm={() => void act(m, () => api.remove(m.id), `Deleted ${m.name}.`)}
                          >
                            Delete
                          </ConfirmButton>
                        </ActionsRow>
                      )}
                    </tbody>
                  ))}
                </table>
              </TableScroller>
            </div>
          )}
        </>
      )}
      {editing && (
        <MonitorEditor
          target={target}
          initial={editing}
          tenants={tenants}
          systems={systems}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
      {generating && (
        <GenerateDialog
          target={target}
          tenants={tenants}
          systems={systems}
          onOpen={(f) => {
            setGenerating(false);
            setEditing(f);
          }}
          onClose={() => setGenerating(false)}
        />
      )}
      {runTest.element}
    </Page>
  );
}
